import { forEach } from '@tunein/web-utils';
import { EventEmitter } from 'eventemitter3';
import { debug } from '../../utils/logger';
export default class BaseEngine extends EventEmitter {
    static name = 'html5';
    #isReady = false;
    #isPlaying = false;
    #duration = 0;
    _options;
    _element;
    _playerEvents;
    _mediaSource;
    constructor(options) {
        super();
        this._options = options;
        this._playerEvents = {
            abort: this.#onEvent('abort'),
            canplay: this.#onEvent('canplay'),
            canplaythrough: this.#onEvent('canplaythrough'),
            durationchange: this.#onEvent('durationchange'),
            emptied: this.#onEmptied,
            ended: this.#onEnded,
            error: this.#onError,
            loadeddata: this.#onEvent('loadeddata'),
            loadedmetadata: this.#onEvent('loadedmetadata'),
            loadstart: this.#onEvent('loadstart'),
            pause: this.#onPause,
            play: this.#onEvent('play'),
            playing: this.#onPlaying,
            progress: this.#onEvent('progress'),
            ratechange: this.#onEvent('ratechange'),
            seeked: this.#onEvent('seeked'),
            seeking: this.#onEvent('seeking'),
            stalled: this.#onEvent('stalled'),
            suspend: this.#onEvent('suspend'),
            timeupdate: this.#onEvent('timeupdate'),
            volumechange: this.#onEvent('volumechange'),
            waiting: this.#onEvent('waiting'),
        };
    }
    static canPlayType(mimeType) {
        const el = document.createElement('audio');
        return Boolean(el.canPlayType(mimeType));
    }
    setOptions(options) {
        this._options = { ...this._options, ...options };
    }
    setup() {
        this._element = this.#createElement();
        this.#registerPlayerEvents();
        this.#registerTextTrackEvents();
        this.#isReady = true;
    }
    get isReady() {
        return this.#isReady;
    }
    get name() {
        return this.constructor.name;
    }
    #createElement() {
        if (!this._element) {
            this._element = this.#createMediaElement(this._options.mediaElementId);
        }
        return this._element;
    }
    #setMediaSource(el, onError) {
        if (!this._mediaSource) {
            return;
        }
        const isManagedMediaSource = this._mediaSource.constructor.name === 'ManagedMediaSource';
        if (!('srcObject' in el) || isManagedMediaSource) {
            el.disableRemotePlayback = true;
            el.src = URL.createObjectURL(this._mediaSource);
            return;
        }
        try {
            el.srcObject = this._mediaSource;
        }
        catch (error) {
            if (error.name !== 'TypeError') {
                this.emit('error', error);
                onError();
                return;
            }
            el.src = URL.createObjectURL(this._mediaSource);
        }
    }
    getElement() {
        return this._element;
    }
    load() {
        const el = this.#createElement();
        return new Promise((resolve, reject) => {
            if (el) {
                const onLoadedMetadata = () => {
                    this.#duration = el.duration;
                    if (this._options.startPosition) {
                        el.currentTime = this._options.startPosition;
                    }
                };
                const onCanPlay = () => {
                    if (this._options.playbackRate) {
                        this.setPlaybackRate(this.isLive() ? 1 : this._options.playbackRate);
                    }
                    el.removeEventListener('error', onError);
                    el.removeEventListener('canplay', onCanPlay);
                    el.removeEventListener('loadedmetadata', onLoadedMetadata);
                    resolve();
                };
                const onError = () => {
                    el.removeEventListener('error', onError);
                    el.removeEventListener('canplay', onCanPlay);
                    el.removeEventListener('loadedmetadata', onLoadedMetadata);
                    reject();
                };
                el.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
                el.addEventListener('canplay', onCanPlay, { once: true });
                el.addEventListener('error', onError, { once: true });
                if (this._mediaSource) {
                    this.#setMediaSource(el, onError);
                }
                else {
                    el.src = this._options.src;
                }
                el.load();
            }
        });
    }
    play() {
        debug('Requested playback');
        const { promise, resolve, reject } = Promise.withResolvers();
        const el = this.getElement();
        let errorEmitted = false;
        if (!el) {
            reject();
            return promise;
        }
        const onPlaying = () => {
            el.removeEventListener('error', onError);
            el.removeEventListener('playing', onPlaying);
            resolve();
        };
        const onError = () => {
            errorEmitted = true;
            el.removeEventListener('error', onError);
            el.removeEventListener('playing', onPlaying);
            reject();
        };
        const onPlayError = (error) => {
            setTimeout(() => {
                if (!errorEmitted) {
                    document.addEventListener('touchend', this.#onTouchEnd);
                    this.emit('error', error);
                }
                onError();
            });
        };
        el.addEventListener('playing', onPlaying, { once: true });
        el.addEventListener('error', onError, { once: true });
        el.play()?.catch(onPlayError);
        return promise;
    }
    stop() {
        const el = this.getElement();
        try {
            if (this._mediaSource) {
                URL.revokeObjectURL(el.src);
            }
            el?.removeAttribute('src');
            el?.load();
        }
        catch {
        }
        this.#duration = 0;
    }
    unload() {
        this.stop();
        this.#deregisterPlayerEvents();
        this.#deregisterTextTrackEvents();
        if (!this._options.keepMediaElementAlive) {
            this.getElement()?.remove();
        }
        this._element = undefined;
        this.#isReady = false;
    }
    getBuffered() {
        return this.getElement()?.buffered;
    }
    getSeekable() {
        return this.getElement()?.seekable;
    }
    getDuration() {
        return this.#duration || this.getElement()?.duration || 0;
    }
    getCurrentTime() {
        return this.getElement()?.currentTime || 0;
    }
    getPlaybackRate() {
        return this.getElement()?.playbackRate || 1;
    }
    getVolume() {
        return this.getElement()?.volume || 0;
    }
    getTextTracks() {
        return this.getElement()?.textTracks;
    }
    isLive() {
        const duration = this.getDuration();
        return duration === Number.POSITIVE_INFINITY || duration === 0;
    }
    isPlaying() {
        return this.#isPlaying;
    }
    pause() {
        this.getElement()?.pause();
    }
    setVolume(level) {
        const el = this.getElement();
        if (el) {
            el.volume = level;
        }
    }
    setPlaybackRate(rate) {
        const el = this.getElement();
        if (el) {
            el.playbackRate = rate;
        }
    }
    mute(value) {
        const el = this.getElement();
        if (el) {
            el.muted = value;
        }
    }
    seekTo(seekTime) {
        const el = this.getElement() || {};
        if ('fastSeek' in el) {
            el.fastSeek(seekTime);
            return;
        }
        if (seekTime < el.duration) {
            el.currentTime = seekTime;
        }
        else {
            el.currentTime = el.duration;
        }
    }
    seekForward(seekOffset) {
        const el = this.getElement();
        if (el) {
            this.seekTo(el.currentTime + seekOffset);
        }
    }
    seekBackward(seekOffset) {
        const el = this.getElement();
        if (el) {
            this.seekTo(el.currentTime - seekOffset);
        }
    }
    #createMediaElement(playerId) {
        let el = document.getElementById(playerId);
        if (!el) {
            el = document.createElement('audio');
            el.setAttribute('id', playerId);
            document.body.appendChild(el);
        }
        return el;
    }
    #onEvent = (eventType) => () => {
        this.emit(eventType);
    };
    #onEmptied = () => {
        this.#isPlaying = false;
        this.emit('emptied');
    };
    #onEnded = () => {
        this.#isPlaying = false;
        this.emit('ended');
    };
    #onPlaying = () => {
        this.#isPlaying = true;
        this.emit('playing');
    };
    #onPause = () => {
        this.#isPlaying = false;
        this.emit('pause');
    };
    #onError = () => {
        const { error } = this.getElement() || {};
        this.emit('error', error);
    };
    #onTouchEnd = () => {
        debug('[document.touchend] Unlocking autoplay');
        this.emit('unlock');
        document.removeEventListener('touchend', this.#onTouchEnd);
    };
    #registerPlayerEvents() {
        debug('Registering player event listeners...');
        if (this._playerEvents) {
            forEach(this._playerEvents, (value, key) => {
                if (value) {
                    this.getElement()?.addEventListener(key, value);
                }
            });
        }
    }
    #deregisterPlayerEvents() {
        debug('Deregistering player event listeners...');
        if (this._playerEvents) {
            forEach(this._playerEvents, (value, key) => {
                if (value) {
                    this.getElement()?.removeEventListener(key, value);
                }
            });
        }
    }
    #onTextTrackCueChange = (event) => {
        const { activeCues, id, kind, label } = event.target;
        this.emit('texttrackcuechange', {
            activeCues,
            track: {
                id,
                kind,
                label,
            },
        });
    };
    #onAddTrack = ({ track }) => {
        debug(`TextTrack added of kind: ${track?.kind}, label: ${track?.label}`);
        track?.addEventListener('cuechange', this.#onTextTrackCueChange);
    };
    #onRemoveTrack = ({ track }) => {
        track?.removeEventListener('cuechange', this.#onTextTrackCueChange);
    };
    #registerTextTrackEvents() {
        const el = this.getElement();
        if (el) {
            el.textTracks?.addEventListener('addtrack', this.#onAddTrack);
            el.textTracks?.addEventListener('removetrack', this.#onRemoveTrack);
        }
    }
    #deregisterTextTrackEvents() {
        const el = this.getElement();
        if (!el) {
            return;
        }
        const trackCount = el.textTracks?.length;
        if (trackCount > 0) {
            for (let i = 0; i < trackCount; i++) {
                el.textTracks?.[i]?.removeEventListener('cuechange', this.#onTextTrackCueChange);
            }
        }
        el.textTracks?.removeEventListener('addtrack', this.#onAddTrack);
        el.textTracks?.removeEventListener('removetrack', this.#onRemoveTrack);
    }
}
